// src/client/pages/userscript-download.tsx
import { Button } from "@client/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@client/components/ui/card";
import { Badge } from "@client/components/ui/badge";
import { Download, ExternalLink, CheckCircle2, AlertCircle, Info, Code2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@client/components/ui/alert";
import { useAuth } from "@client/contexts/auth-context";
import { API_BASE, USERSCRIPT_DOWNLOAD_ENDPOINT, USERSCRIPT_VERSION, HOST_URL, BASE_PATH } from "../../client-config";

export default function UserscriptDownload() {
  const { user } = useAuth();

  const handleDownloadZip = () => {
    window.location.href = `${API_BASE}${USERSCRIPT_DOWNLOAD_ENDPOINT}`;
  };

  const libraryUrl = `${HOST_URL}${BASE_PATH}/tm-application.js`;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Userscript Installation</h1>
        <p className="text-muted-foreground mt-2">
          Download your personalized TM Application userscript for Tampermonkey with embedded authentication.
        </p>
      </div>

      {/* User Info Alert */}
      {user && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Personalized for: {user.username}</AlertTitle>
          <AlertDescription>
            The userscript ZIP package will be generated with your access credentials embedded. 
            Do not share this package with others.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Download Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Download Personalized Package
            </CardTitle>
            <CardDescription>
              Complete Tampermonkey package with userscript, configuration, and storage
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary">Version {USERSCRIPT_VERSION}</Badge>
              <Badge variant="outline">Authenticated</Badge>
              <Badge variant="outline">Ready to Import</Badge>
            </div>
            
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                The ZIP contains your personal access code for secure API communication. 
                All settings are pre-configured for immediate use.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <Button onClick={handleDownloadZip} className="w-full" size="lg">
                <Download className="mr-2 h-4 w-4" />
                Download Userscript ZIP
              </Button>
              
              <p className="text-xs text-muted-foreground text-center">
                Package includes: userscript, Tampermonkey options, and storage configuration
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Installation Steps */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              Installation Steps
            </CardTitle>
            <CardDescription>
              Import the complete package into Tampermonkey
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="space-y-4 text-sm">
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium">
                  1
                </span>
                <div>
                  <p className="font-medium">Install Tampermonkey</p>
                  <p className="text-muted-foreground">
                    Get the Tampermonkey extension for your browser if not installed
                  </p>
                  <a 
                    href="https://www.tampermonkey.net/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1"
                  >
                    Visit Tampermonkey.net <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </li>
              
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium">
                  2
                </span>
                <div>
                  <p className="font-medium">Download Your Package</p>
                  <p className="text-muted-foreground">
                    Click "Download Userscript ZIP" above to get your personalized package
                  </p>
                </div>
              </li>
              
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium">
                  3
                </span>
                <div>
                  <p className="font-medium">Import in Tampermonkey</p>
                  <p className="text-muted-foreground">
                    Tampermonkey Dashboard → Utilities → ZIP → Import from file
                  </p>
                </div>
              </li>
              
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium">
                  4
                </span>
                <div>
                  <p className="font-medium">Verify Installation</p>
                  <p className="text-muted-foreground">
                    Navigate to LinkedIn - the TM interface should appear automatically
                  </p>
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>
      </div>

      {/* Technical Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code2 className="h-5 w-5" />
            Technical Architecture
          </CardTitle>
          <CardDescription>
            How the userscript integrates with TM Application
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>@require Architecture:</strong> The userscript loads the TM Application library 
                from this server via <code className="text-xs bg-muted px-1 py-0.5 rounded">@require</code>. 
                This means updates to features are automatic - no reinstallation needed.
              </AlertDescription>
            </Alert>
            
            <div className="grid gap-3 text-sm">
              <div className="flex flex-col gap-1">
                <strong>Library URL:</strong>
                <code className="text-xs rounded bg-muted px-2 py-1.5 break-all">
                  {libraryUrl}
                </code>
              </div>
              
              <div className="flex flex-col gap-1">
                <strong>Authentication:</strong>
                <p className="text-muted-foreground">
                  Your access code is embedded in the userscript and automatically included in API requests
                </p>
              </div>
              
              <div className="flex flex-col gap-1">
                <strong>Automatic Updates:</strong>
                <p className="text-muted-foreground">
                  When we update the server-side library, your userscript automatically gets new features
                </p>
              </div>

              <div className="flex flex-col gap-1">
                <strong>Supported Pages:</strong>
                <p className="text-muted-foreground">
                  LinkedIn Search, Profiles, Groups, Events, Network - detected automatically
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Available Features</CardTitle>
          <CardDescription>
            LinkedIn automation capabilities provided by the userscript
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <h4 className="font-medium">LinkedIn Scraper</h4>
              <p className="text-sm text-muted-foreground">
                Extract profiles from search results with smart page detection
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium">Event Inviter</h4>
              <p className="text-sm text-muted-foreground">
                Automate LinkedIn event invitations and attendee scraping
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium">Group Messenger</h4>
              <p className="text-sm text-muted-foreground">
                Send bulk messages to LinkedIn group members
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium">Single Capture</h4>
              <p className="text-sm text-muted-foreground">
                Quick profile capture from individual LinkedIn pages
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium">Network Scraper</h4>
              <p className="text-sm text-muted-foreground">
                Analyze and extract your LinkedIn network connections
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium">Real-time Sync</h4>
              <p className="text-sm text-muted-foreground">
                Immediate synchronization with TM Application database
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Notice */}
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Security Notice</AlertTitle>
        <AlertDescription>
          Your userscript package contains your personal access credentials. Never share this file 
          with others or upload it to public repositories. If compromised, you can regenerate your 
          access code in the Settings.
        </AlertDescription>
      </Alert>
    </div>
  );
}